/**
 * List all possible values for StirVerstat
 *
 * Note that the StirVerstat parameter / X-Twilio-VerStat header are only present for incoming calls with SHAKEN PASSporT identity headers.
 *
 */
export enum TwilioStirVerstat {
  /**
   * Twilio received the SIP INVITE, with a SHAKEN PASSporT, and was able to fetch the public certificate of the originating service provider from the Certificate Authority that signed the call to verify that no one tampered with the SIP INVITE during transit.
   *
   *     Attestation level A
   */
  TNValidationPassedA = 'TN-Validation-Passed-A',

  /**
   * Twilio received the SIP INVITE, with a SHAKEN PASSporT, and was able to fetch the public certificate of the originating service provider from the Certificate Authority that signed the call to verify that no one tampered with the SIP INVITE during transit.
   *
   *     Attestation level B
   */
  TNValidationPassedB = 'TN-Validation-Passed-B',

  /**
   * Twilio received the SIP INVITE, with a SHAKEN PASSporT, and was able to fetch the public certificate of the originating service provider from the Certificate Authority that signed the call to verify that no one tampered with the SIP INVITE during transit.
   *
   *     Attestation level C
   */
  TNValidationPassedC = 'TN-Validation-Passed-C',

  /**
   * Twilio was unable to verify the contents of the SHAKEN PASSporT.
   *
   *     This could mean tampering, or simply that Twilio could not retrieve the public certificate of the originating service provider from the Certificate Authority.
   *
   *     Attestation level A
   */
  TNValidationFailedA = 'TN-Validation-Failed-A',

  /**
   * Twilio was unable to verify the contents of the SHAKEN PASSporT.
   *
   *     This could mean tampering, or simply that Twilio could not retrieve the public certificate of the originating service provider from the Certificate Authority.
   *
   *     Attestation level B
   */
  TNValidationFailedB = 'TN-Validation-Failed-B',

  /**
   * Twilio was unable to verify the contents of the SHAKEN PASSporT.
   *
   *     This could mean tampering, or simply that Twilio could not retrieve the public certificate of the originating service provider from the Certificate Authority.
   *
   *     Attestation level C
   */
  TNValidationFailedC = 'TN-Validation-Failed-C',

  /**
   * Possible causes:
   *
   *     A malformed E.164 phone number
   * SHAKEN PASSporT format is invalid. It should consist of a header, payload, signature, and parameters.
   *     SHAKEN PASSporT does not have required fields like ppt headers or info parameter
   * SHAKEN PASSporT orig field doesn't match with actual callerid in the SIP messages (P-Asserted-Identity, Remote-Party-Identity, or From header).
   * SHAKEN PASSporT dest field doesn't match with the actual destination of the call in the SIP Request-URI.
   * SHAKEN PASSporT iat field is too old – more than 1 minutes from current timestamp or the SIP Date header value.
   */
  NoTNValidation = 'No-TN-Validation',

  /**
   * Twilio was unable to verify the contents of the SHAKEN PASSporT.
   *
   *     This could mean tampering, or simply that Twilio could not retrieve the public certificate of the originating service provider from the Certificate Authority.
   *
   *     No attestation level determined.
   */
  TNValidationFailed = 'TN-Validation-Failed',

  /**
   * Twilio was unable to verify the contents of the SHAKEN PASSporT.
   *
   * This could mean tampering or that Twilio could not retrieve the public certificate of the originating service provider from the Certificate Authority.
   *
   * No attestation level determined.
   */
  NULL = 'NULL',

  /**
   * Twilio received the SIP INVITE, with a SHAKEN PASSporT, and was able to fetch the public certificate of the Diverting service provider from the Certificate Authority that signed the call.
   *
   * This verifies that no one tampered with the SIP INVITE during transit.
   *
   * Attestation level A
   */
  TNValidationPassedADiverted = 'TN-Validation-Passed-A-Diverted',

  /**
   * Twilio received the SIP INVITE, with a SHAKEN PASSporT, and was able to fetch the public certificate of the Diverting service provider from the Certificate Authority that signed the call.
   *
   * This verifies that no one tampered with the SIP INVITE during transit.
   *
   * Attestation level B
   */
  TNValidationPassedBDiverted = 'TN-Validation-Passed-B-Diverted',

  /**
   * Twilio received the SIP INVITE, with a SHAKEN PASSporT, and was able to fetch the public certificate of the Diverting service provider from the Certificate Authority that signed the call.
   *
   * This verifies that no one tampered with the SIP INVITE during transit.
   *
   * Attestation level C
   */
  TNValidationPassedCDiverted = 'TN-Validation-Passed-C-Diverted',

  /**
   * Twilio was unable to verify the contents of the SHAKEN PASSporT.
   *
   * This could mean tampering or that Twilio could not retrieve the public certificate of the Diverting service provider from the Certificate Authority.
   *
   * Attestation level A
   */
  TNValidationFailedADiverted = 'TN-Validation-Failed-A-Diverted',

  /**
   * Twilio was unable to verify the contents of the SHAKEN PASSporT.
   *
   * This could mean tampering or that Twilio could not retrieve the public certificate of the Diverting service provider from the Certificate Authority.
   *
   * Attestation level B
   */
  TNValidationFailedBDiverted = 'TN-Validation-Failed-B-Diverted',

  /**
   * Twilio was unable to verify the contents of the SHAKEN PASSporT.
   *
   * This could mean tampering or that Twilio could not retrieve the public certificate of the Diverting service provider from the Certificate Authority.
   *
   * Attestation level C
   */
  TNValidationFailedCDiverted = 'TN-Validation-Failed-C-Diverted',

  /**
   * Twilio received the SIP INVITE for a Passport Passthrough call, with a SHAKEN PASSporT, and was able to fetch the public certificate of the originating service provider from the Certificate Authority that signed the call to verify that no one tampered with the SIP INVITE.
   *
   * This verifies that no one tampered with the SIP INVITE during transit.
   *
   * Attestation level A
   */
  TNValidationPassedAPassthrough = 'TN-Validation-Passed-A-Passthrough',

  /**
   * Twilio received the SIP INVITE for a Passport Passthrough call, with a SHAKEN PASSporT, and was able to fetch the public certificate of the originating service provider from the Certificate Authority that signed the call to verify that no one tampered with the SIP INVITE.
   *
   * This verifies that no one tampered with the SIP INVITE during transit.
   *
   * Attestation level B
   */
  TNValidationPassedBPassthrough = 'TN-Validation-Passed-B-Passthrough',

  /**
   * Twilio received the SIP INVITE for a Passport Passthrough call, with a SHAKEN PASSporT, and was able to fetch the public certificate of the originating service provider from the Certificate Authority that signed the call to verify that no one tampered with the SIP INVITE.
   *
   * This verifies that no one tampered with the SIP INVITE during transit.
   *
   * Attestation level C
   */
  TNValidationPassedCPassthrough = 'TN-Validation-Passed-C-Passthrough',

  /**
   * Twilio was unable to verify the contents of the SHAKEN PASSporT for the passthrough call.
   *
   * This could mean tampering or that Twilio could not retrieve the public certificate of the originating service provider from the Certificate Authority.
   *
   * Attestation level A
   */
  TNValidationFailedAPassthrough = 'TN-Validation-Failed-A-Passthrough',

  /**
   * Twilio was unable to verify the contents of the SHAKEN PASSporT for the passthrough call.
   *
   * This could mean tampering or that Twilio could not retrieve the public certificate of the originating service provider from the Certificate Authority.
   *
   * Attestation level B
   */
  TNValidationFailedBPassthrough = 'TN-Validation-Failed-B-Passthrough',

  /**
   * Twilio was unable to verify the contents of the SHAKEN PASSporT for the passthrough call.
   *
   * This could mean tampering or that Twilio could not retrieve the public certificate of the originating service provider from the Certificate Authority.
   *
   * Attestation level C
   */
  TNValidationFailedCPassthrough = 'TN-Validation-Failed-C-Passthrough',
}
